import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import { formatDate, formatReportingDateGhgEntry } from "../../../utils/formatDate";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import BulkUploadSkippedDialog from "../../../Components/Common/BulkUploadSkippedDialog";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import BulkUploadReview from "./BulkUploadReview";
import { useUser } from "../../../context/AuthContext";

/** Bulk template columns only — skipped-entry Excel matches this. */
const INVESTMENT_SKIPPED_DOWNLOAD_COLUMNS = [
  "Investee/Project Name",
  "Reporting Period Start Year (YYYY)",
  "Reporting Period End Year (YYYY)",
  "Scope 1 Emissions (tCO2e)",
  "Scope 2 Emissions (tCO2e)",
  "Scope 3 Emissions (tCO2e)",
  "Remark",
];

const Investments = () => {
  const { user } = useUser();
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [filters, setFilters] = useState({
    investmentType: [],
    investeeProjectName: [],
    countryOfOperation: [],
  });
  const [investmentTypeOptions, setInvestmentTypeOptions] = useState([]);
  const [investmentTypeDetailsMap, setInvestmentTypeDetailsMap] = useState({});
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [skippedUploadDialog, setSkippedUploadDialog] = useState({
    open: false,
    skippedEntries: [],
    inserted: 0,
    skipped: 0,
  });

  const [cardData, setCardData] = useState({
    totalEntries: 0,
    totalInvestmentTypes: 0,
    totalInvestmentSectors: 0,
    totalCountryOfOperations: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = [
    { key: "createdAt", label: "Date", sortable: true, width: "12%" },
    {
      key: "investeeProjectName",
      label: "Investee/Project Name",
      sortable: true,
      width: "21%",
    },
    { key: "investmentType", label: "Investment Type", sortable: true, width: "21%" },
    {
      key: "countryOfOperation",
      label: "Country of operation",
      sortable: true,
      width: "15%",
    },
    { key: "reportingPeriod", label: "Reporting Period", sortable: false, width: "20%" },
  ];

  const inputData = [
    { fieldName: "investeeProjectName", title: "Investee/Project Name" },
    { fieldName: "investmentType", title: "Investment Type" },
    {
      fieldName: "sector",
      title: "Sector(s) the investee company operates in",
    },
    { fieldName: "countryOfOperation", title: "Country of operation" },
    { fieldName: "equityHoldingPercent", title: "Equity Holding (%)" },
    { fieldName: "reportingStart", title: "Reporting Period Start Date" },
    { fieldName: "reportingEnd", title: "Reporting Period End Date" },
    {
      fieldName: "scope1EmissionsMetricTon",
      title: "Scope 1 Emissions (tCO2e)",
    },
    {
      fieldName: "scope2EmissionsMetricTon",
      title: "Scope 2 Emissions (tCO2e)",
    },
    {
      fieldName: "scope3EmissionsMetricTon",
      title: "Scope 3 Emissions (tCO2e)",
    },
    { fieldName: "remark", title: "Remark (optional)" },
    { fieldName: "editRemarkReason", title: "Edit Remark Reason" },
  ];

  const filterData = [
    {
      title: "Date",
      type: "date",
      key: "date",
      value: [],
    },
    {
      title: "Reporting Period",
      type: "reportingYearRange",
      key: "reportingPeriod",
      value: [],
    },
    {
      title: "Investee/Project Name",
      type: "option",
      key: "investeeProjectName",
      value: filters?.investeeProjectName || [],
    },
    {
      title: "Investment Type",
      type: "option",
      key: "investmentType",
      value: filters?.investmentType || [],
    },
    {
      title: "Country of operation",
      type: "option",
      key: "countryOfOperation",
      value: filters?.countryOfOperation || [],
    },
  ];

  const fetchData = async () => {
    try {
      const filter = { ...filterObj };

      // Apply date range filter (same pattern as other Scope 3 modules)
      if (filter.date) {
        filter.startDate = filter?.date?.start;
        filter.endDate = filter?.date?.end || new Date();
        filter.dateField = "createdAt";
        delete filter.date;
      }
      if (filter.reportingPeriod) {
        const startYear = Number(filter?.reportingPeriod?.start);
        const endYear = Number(filter?.reportingPeriod?.end);
        if (!Number.isNaN(startYear)) {
          filter.reportingStart = `${startYear}-04-01`;
        }
        if (!Number.isNaN(endYear)) {
          filter.reportingEnd = `${endYear}-03-31`;
        }
        delete filter.reportingPeriod;
      }

      // Remove empty filters and normalize array filters
      Object.keys(filter).forEach((key) => {
        if (Array.isArray(filter[key])) {
          if (filter[key].length > 0) {
            filter[key] = filter[key].join(",");
          } else {
            delete filter[key];
          }
        } else if (
          filter[key] === undefined ||
          filter[key] === null ||
          filter[key] === ""
        ) {
          delete filter[key];
        }
      });

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(`/scope3/investments/get?${queryParams}`);

      const arr =
        res?.data?.data?.map((item) => {
          const start = item?.reportingStart;
          const end = item?.reportingEnd;
          let reportingPeriod = "";

          if (start || end) {
            const startStr = start ? formatReportingDateGhgEntry(start) : "";
            const endStr = end ? formatReportingDateGhgEntry(end) : "";
            reportingPeriod = [startStr, endStr].filter(Boolean).join(" - ");
          }

          return {
            investmentType: item?.investmentType,
            investeeProjectName: item?.investeeProjectName,
            sector: item?.sector,
            countryOfOperation: item?.countryOfOperation,
            reportingPeriod,
            action: "View",
            id: item?._id,
            ...item,
          };
        }) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const [cardRes, filterRes] = await Promise.all([
        get(`/scope3/investments/cardData`),
        get(`/scope3/investments/filter`),
      ]);

      const data = cardRes?.data?.data || {};
      const filterData = filterRes?.data?.data || {};

      setCardData({
        totalEntries: data?.totalEntries || 0,
        totalInvestmentTypes:
          data?.totalInvestmentTypes || filterData?.investmentType?.length || 0,
        totalInvestmentSectors: data?.totalInvestmentSectors ?? 0,
        totalCountryOfOperations:
          data?.totalCountryOfOperations ||
          filterData?.countryOfOperation?.length ||
          0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const [res, listRes] = await Promise.all([
        get(`/scope3/investments/filter`),
        get(`/scope3/investments/get?page=1&limit=10000`),
      ]);
      const { investmentType, countryOfOperation } = res?.data?.data || {};
      const listData = Array.isArray(listRes?.data?.data) ? listRes.data.data : [];
      const investeeProjectName = [
        ...new Set(
          listData
            .map((item) => item?.investeeProjectName)
            .filter((name) => String(name || "").trim())
        ),
      ];

      setFilters({
        investmentType: investmentType || [],
        investeeProjectName,
        countryOfOperation: countryOfOperation || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchInvestmentMasterTypes = async () => {
    try {
      const res = await get(
        `/master/investmentMasters?page=1&limit=1000&sortField=createdAt&sortOrder=desc`
      );
      const masters = Array.isArray(res?.data?.data) ? res.data.data : [];

      const seen = new Set();
      const options = [];
      const detailsMap = {};

      masters.forEach((item) => {
        const projectName = item?.investeeProjectName;
        if (!projectName || seen.has(projectName)) return;
        seen.add(projectName);
        options.push(projectName);
        detailsMap[projectName] = {
          _id: item?._id || "",
          investmentType: item?.investmentType || "",
          investeeProjectName: item?.investeeProjectName || "",
          sector: item?.sector || "",
          countryOfOperation: item?.countryOfOperation || "",
          equityHoldingPercent:
            item?.equityHoldingPercent !== undefined &&
              item?.equityHoldingPercent !== null
              ? String(item.equityHoldingPercent)
              : "",
        };
      });

      setInvestmentTypeOptions(options);
      setInvestmentTypeDetailsMap(detailsMap);
    } catch (error) {
      console.error("Error fetching investment master types:", error);
      setInvestmentTypeOptions([]);
      setInvestmentTypeDetailsMap({});
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchInvestmentMasterTypes();
  }, []);

  const bannerData = [
    {
      title: "Total Investments Type",
      description: `${cardData?.totalInvestmentTypes ?? 0}`,
    },
    {
      title: "Total Investment sectors",
      description: `${cardData?.totalInvestmentSectors ?? 0}`,
    },
    {
      title: "Total Country of Operations",
      description: `${cardData?.totalCountryOfOperations ?? 0}`,
    },
  ];

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchExcelData = async () => {
    try {
      const res = await get(
        `/master/investmentMasters?page=1&limit=1000&sortField=createdAt&sortOrder=desc`
      );
      const masters = Array.isArray(res?.data?.data) ? res.data.data : [];

      const template = masters.length
        ? masters.map((item) => ({
          "Investee/Project Name": item?.investeeProjectName || "",
          "Reporting Period Start Year (YYYY)": "",
          "Reporting Period End Year (YYYY)": "",
          "Scope 1 Emissions (tCO2e)": "",
          "Scope 2 Emissions (tCO2e)": "",
          "Scope 3 Emissions (tCO2e)": "",
          Remark: "",
        }))
        : [
          {
            "Investee/Project Name": "",
            "Reporting Period Start Year (YYYY)": "",
            "Reporting Period End Year (YYYY)": "",
            "Scope 1 Emissions (tCO2e)": "",
            "Scope 2 Emissions (tCO2e)": "",
            "Scope 3 Emissions (tCO2e)": "",
            Remark: "",
          },
        ];

      setExcelData(template);
    } catch (error) {
      console.error("Error generating investments bulk upload template:", error);
      setExcelData([
        {
          "Investee/Project Name": "",
          "Reporting Period Start Year (YYYY)": "",
          "Reporting Period End Year (YYYY)": "",
          "Scope 1 Emissions (tCO2e)": "",
          "Scope 2 Emissions (tCO2e)": "",
          "Scope 3 Emissions (tCO2e)": "",
          Remark: "",
        },
      ]);
    }
  };

  const uploadErrorClose = () => {
    setBulkUploadTable(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(false);
    setMatchData([]);
    setFormDataBulkUpload([]);
    fetchData();
    fetchCardData();
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      void fetchInvestmentMasterTypes();
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      void fetchInvestmentMasterTypes();
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/investments/update?Id=${editData.id || editData._id}`
        : `/scope3/investments/add`;

      const apiCall = isEditing ? put : post;

      const res = await apiCall(url, data);

      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });

        setIsModalOpen(false);

        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }

        fetchCardData();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });

      console.error("Error in handleSubmit (Investments):", error);
    }
  };

  const handleDeleteOpen = (row) => {
    setDeletedItemId(row);
    setDeleteDialog(true);
  };

  const handleDeleteAgree = async () => {
    try {
      await del(`/scope3/investments/delete`, {
        params: {
          Id: deletedItemId?._id,
        },
      });
      setDeleteDialog(false);
      setsnackOpen({
        open: true,
        message: "Deleted successfully",
        severity: "success",
      });
      fetchData();
      fetchCardData();
    } catch (err) {
      console.error(err);
      setsnackOpen({
        open: true,
        message: err.response?.data?.message ?? "Network Error",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Cards data={bannerData} title={"Investments"} />

      <ActionButtons
        onFilterClick={() => {
          fetchFilters();
          setIsFilterModalOpen(true);
        }}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"Investments"}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        headers={headers}
        data={tableData ?? []}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        actionColumnWidthPx={72}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={handleEdit}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={editModal ? "Edit Investment" : "Add Investment"}
        initialData={editData}
        isEditing={editModal}
        investmentTypeOptions={investmentTypeOptions}
        investmentTypeDetailsMap={investmentTypeDetailsMap}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"Investments"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?._id,
            url: `${api}/scope3/investments/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={handleCloseBulkUpload}
          setMatchData={setMatchData}
          setFormDataBulkUpload={setFormDataBulkUpload}
          matchedKey="investmentStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"investments"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/investments/bulkupload"}
        />
      )}

      {bulkUploadTable && (
        <BulkUploadReview
          bulkUploadTable={bulkUploadTable}
          matchData={matchData}
          setMatchData={setMatchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setsnackOpen={setsnackOpen}
          bulkUploadUrl={"/scope3/investments/jsonupload"}
          onClose={() => setBulkUploadTable(false)}
          onConfirm={(data) => {
            setBulkUploadTable(false);
            setMatchData([]);
            setFormDataBulkUpload([]);
            fetchData();
            fetchCardData();
            void fetchInvestmentMasterTypes();
            void fetchExcelData();

            const skippedEntries = data?.skippedEntries;
            const hasSkipped =
              Array.isArray(skippedEntries) && skippedEntries.length > 0;
            const inserted = Number(data?.inserted ?? 0);
            const skipped = Number(data?.skipped ?? 0);

            if (hasSkipped) {
              setSkippedUploadDialog({
                open: true,
                skippedEntries,
                inserted,
                skipped,
              });
            }
          }}
        />
      )}

      <BulkUploadSkippedDialog
        open={skippedUploadDialog.open}
        skippedEntries={skippedUploadDialog.skippedEntries}
        inserted={skippedUploadDialog.inserted}
        skipped={skippedUploadDialog.skipped}
        downloadFileName="investments_skipped"
        downloadExcelColumns={INVESTMENT_SKIPPED_DOWNLOAD_COLUMNS}
        onClose={() =>
          setSkippedUploadDialog({
            open: false,
            skippedEntries: [],
            inserted: 0,
            skipped: 0,
          })
        }
        // onDownload={(rows) => setExcelData(rows)}
      />
    </>
  );
};

export default Investments;

